import { Flex, TextInput, Combobox, Select } from '@applyboard/crystal-ui';

interface ConversionTableMetadataFormProps {
  value: {
    name: string;
    destinationGradeType: 'numeric' | 'letter';
    sourceInstitutionName: string;
    sourceCountryCode: string;
    gradeSchemaId: string;
    sourceEducationLevelId: string;
  };
  countries: Array<{ code: string; label: string }>;
  gradeSchemas: Array<{
    id: string;
    name: string;
    gradeSchemaType: 'numeric' | 'letter';
    minGrade?: number;
    maxGrade?: number;
    validGrades?: string[];
  }>;
  educationLevels: Array<{ id: string; name: string }>;
  isGradeSchemasLoading: boolean;
  isReadOnly?: boolean;
  disabledFields?: Partial<Record<keyof ConversionTableMetadataFormProps['value'], boolean>>;
  onChange: <K extends keyof ConversionTableMetadataFormProps['value']>(
    key: K,
    value: ConversionTableMetadataFormProps['value'][K]
  ) => void;
}

export function ConversionTableMetadataForm({
  value,
  countries,
  gradeSchemas,
  educationLevels,
  isGradeSchemasLoading,
  isReadOnly = false,
  disabledFields,
  onChange,
}: ConversionTableMetadataFormProps) {
  const selectedGradeSchema = gradeSchemas.find((schema) => schema.id === value.gradeSchemaId);
  const isNumericSchema = selectedGradeSchema?.gradeSchemaType === 'numeric';
  const minGradeValue = selectedGradeSchema?.minGrade != null ? String(selectedGradeSchema.minGrade) : '';
  const maxGradeValue = selectedGradeSchema?.maxGrade != null ? String(selectedGradeSchema.maxGrade) : '';
  const validGradesValue = selectedGradeSchema?.validGrades?.join(', ') ?? '';

  const isFieldDisabled = (key: keyof ConversionTableMetadataFormProps['value']) =>
    isReadOnly || Boolean(disabledFields?.[key]);

  const handleChange = <K extends keyof ConversionTableMetadataFormProps['value']>(
    key: K,
    newValue: ConversionTableMetadataFormProps['value'][K]
  ) => {
    if (isFieldDisabled(key)) {
      return;
    }
    onChange(key, newValue);
  };

  return (
    <Flex direction="column" gap={6}>
      <Flex direction="row" gap={6} wrap>
        <Flex.Item basis="240px" grow={1}>
          <TextInput
            value={value.name}
            onChange={(val) => handleChange('name', val)}
            label="Conversion table name"
            size="sm"
            disabled={isFieldDisabled('name')}
          />
        </Flex.Item>
      </Flex>

      <Flex direction="row" gap={6} wrap>
        <Flex.Item basis="240px" grow={1}>
          <Select
            value={value.gradeSchemaId}
            onChange={(val) => handleChange('gradeSchemaId', val)}
            size="sm"
            label="Grade schema"
            placeholder={isGradeSchemasLoading ? 'Loading grade schemas...' : 'Select grade schema'}
            disabled={isGradeSchemasLoading || gradeSchemas.length === 0 || isFieldDisabled('gradeSchemaId')}
            appearance="styled"
          >
            {gradeSchemas.map((schema) => (
              <Select.Option
                key={schema.id}
                value={schema.id}
                label={`${schema.name} (${schema.gradeSchemaType === 'letter' ? 'Letter' : 'Numeric'})`}
              />
            ))}
          </Select>
        </Flex.Item>

        <Flex.Item basis="240px" grow={1}>
          <Select
            value={value.destinationGradeType}
            onChange={(val) => handleChange('destinationGradeType', val as 'numeric' | 'letter')}
            size="sm"
            label="Destination grade type"
            appearance="styled"
            disabled={isFieldDisabled('destinationGradeType')}
          >
            <Select.Option value="numeric" label="Numeric" />
            <Select.Option value="letter" label="Letter" />
          </Select>
        </Flex.Item>

        {selectedGradeSchema ? (
          <>
            {isNumericSchema ? (
              <>
                <Flex.Item basis="240px" grow={1}>
                  <TextInput label="Min grade" size="sm" value={minGradeValue} disabled onChange={() => {}} />
                </Flex.Item>
                <Flex.Item basis="240px" grow={1}>
                  <TextInput label="Max grade" size="sm" value={maxGradeValue} disabled onChange={() => {}} />
                </Flex.Item>
              </>
            ) : (
              <Flex.Item basis="240px" grow={1}>
                <TextInput label="Valid grades" size="sm" value={validGradesValue} disabled onChange={() => {}} />
              </Flex.Item>
            )}
          </>
        ) : null}
      </Flex>

      <Flex direction="row" gap={6} wrap>
        <Flex.Item basis="240px" grow={1}>
          <Combobox
            value={value.sourceEducationLevelId}
            onChange={(val) => handleChange('sourceEducationLevelId', val)}
            size="sm"
            label="Source Education level"
            placeholder={educationLevels.length === 0 ? 'No education levels available' : 'Select education level'}
            disabled={educationLevels.length === 0 || isFieldDisabled('sourceEducationLevelId')}
          >
            {educationLevels.map((educationLevel) => (
              <Combobox.Option key={educationLevel.id} value={educationLevel.id} label={educationLevel.name} />
            ))}
          </Combobox>
        </Flex.Item>

        <Flex.Item basis="240px" grow={1}>
          <Combobox
            value={value.sourceCountryCode}
            onChange={(val) => handleChange('sourceCountryCode', val)}
            size="sm"
            label="Source country"
            disabled={countries.length === 0 || isFieldDisabled('sourceCountryCode')}
          >
            {countries.map((country) => (
              <Combobox.Option key={country.code} value={country.code} label={country.label} />
            ))}
          </Combobox>
        </Flex.Item>

        <Flex.Item basis="240px" grow={1}>
          <TextInput
            value={value.sourceInstitutionName}
            onChange={(val) => handleChange('sourceInstitutionName', val)}
            label="Source institution name"
            size="sm"
            placeholder="Institution name"
            disabled={isFieldDisabled('sourceInstitutionName')}
          />
        </Flex.Item>
      </Flex>
    </Flex>
  );
}
